import React, {
  createContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from 'react';
import ConfirmModal from './ConfirmModal';
import { setGlobalConfirmFunction } from './confirmService';

type RejectReason = 'Dismiss' | 'Cancel';

export interface ConfirmModalOptions {
  title: string;
  message?: string | React.ReactElement;
  okLabel?: string;
  onExited?: () => void;
  cancelLabel?: string;
  enableEscape?: boolean;
  prompt?: {
    label: string;
    placeholder?: string;
    defaultValue?: string;
    minLength?: number;
    maxLength?: number;
  };
}

export type ConfirmFunction = (options: ConfirmModalOptions) => Promise<string | void>;

const ConfirmModalContext = createContext<ConfirmFunction | undefined>(undefined);

interface ConfirmModalProviderProps {
  children: ReactNode;
}

export const ConfirmModalProvider: React.FC<ConfirmModalProviderProps> = ({ children }) => {
  const [modalOptions, setModalOptions] = useState<ConfirmModalOptions | null>(null);
  const [promiseResolver, setPromiseResolver] = useState<(value?: string) => void>();
  const [promiseRejector, setPromiseRejector] = useState<(reason?: RejectReason) => void>();

  const confirmFn: ConfirmFunction = useCallback((options: ConfirmModalOptions) => {
    return new Promise<string | void>((resolve, reject) => {
      setModalOptions(options);
      setPromiseResolver(() => resolve);
      setPromiseRejector(() => reject);
    });
  }, [setModalOptions, setPromiseResolver, setPromiseRejector]);

  // Set the global confirm function on mount.
  useEffect(() => {
    setGlobalConfirmFunction(confirmFn);
  }, [confirmFn]);

  const handleConfirm = (promptValue?: string) => {
    promiseResolver?.(promptValue);
    modalOptions?.onExited?.();
    cleanup();
  };

  const handleCancel = () => {
    promiseRejector?.('Cancel');
    modalOptions?.onExited?.();
    cleanup();
  };

  const handleDismiss = () => {
    promiseRejector?.('Dismiss');
    modalOptions?.onExited?.();
    cleanup();
  };

  const cleanup = () => {
    const CLEANUP_TIMEOUT = 250; // Wait for animation to complete.
    setTimeout(() => {
      setModalOptions(null);
      setPromiseResolver(undefined);
      setPromiseRejector(undefined);
    }, CLEANUP_TIMEOUT);
  };

  return (
    <ConfirmModalContext.Provider value={confirmFn}>
      {children}
      {modalOptions && (
        <ConfirmModal
          show={!!modalOptions}
          title={modalOptions.title}
          message={modalOptions.message}
          okLabel={modalOptions.okLabel}
          cancelLabel={modalOptions.cancelLabel}
          enableEscape={modalOptions.enableEscape}
          prompt={modalOptions.prompt}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          onDismiss={handleDismiss}
        />
      )}
    </ConfirmModalContext.Provider>
  );
};
